import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import AdminSideMenu from '../components/UI/AdminSideMenu/AdminSideMenu';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkAuthenticatedAdmin } from '../utils/client-functions';
import { setFetching } from '../redux/states';
import { useErrorSetter } from '../hooks/useUiMessages';
import UsersTable from '../components/UI/Tables/UsersTable';
import styles from './Analytics.module.css';
import { Box, Grid, Card, CardContent, Typography, TextField, Select, MenuItem, Button } from '@mui/material';

const UsersDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const setError = useErrorSetter();
    const token = sessionStorage?.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
    const [currentAdminRole, setCurrentAdminRole] = useState('');

    // Data state
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [globalTotal, setGlobalTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(100);
        const [loading, setLoading] = useState(false);
        const [stats, setStats] = useState({ filteredTotal: 0, filteredVerified: 0, filteredRated: 0, newInRange: 0 });

    // Filters
    const [verified, setVerified] = useState(''); // '', 'true', 'false'
    const [rated, setRated] = useState(''); // '', 'true', 'false'
    const [country, setCountry] = useState('');
    const [stateRegion, setStateRegion] = useState('');
    const [startDate, setStartDate] = useState(''); // YYYY-MM-DD
    const [endDate, setEndDate] = useState(''); // YYYY-MM-DD
    const [query, setQuery] = useState('');

    // Sorting (server-driven for stable paging)
        const [sortBy, setSortBy] = useState('createdAt');
        const [sortDir, setSortDir] = useState('desc');

    // Hydrate state from URL on mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        setPage(parseInt(params.get('page') || '1', 10));
        setLimit(parseInt(params.get('limit') || '100', 10));
        setVerified(params.get('verified') ?? '');
        setRated(params.get('rated') ?? '');
        setCountry(params.get('country') ?? '');
        setStateRegion(params.get('state') ?? '');
        setStartDate(params.get('startDate') ?? '');
        setEndDate(params.get('endDate') ?? '');
        setQuery(params.get('q') ?? '');
        setSortBy(params.get('sortBy') || 'createdAt');
        setSortDir(params.get('sortDir') || 'desc');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync URL when state changes
    useEffect(() => {
        const params = new URLSearchParams();
        if (page) params.set('page', String(page));
        if (limit) params.set('limit', String(limit));
        if (verified !== '') params.set('verified', verified);
        if (rated !== '') params.set('rated', rated);
        if (country) params.set('country', country);
        if (stateRegion) params.set('state', stateRegion);
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        if (query) params.set('q', query);
        if (sortBy) params.set('sortBy', sortBy);
        if (sortDir) params.set('sortDir', sortDir);
        const search = params.toString();
        const newUrl = `${location.pathname}?${search}`;
        if (newUrl !== window.location.pathname + window.location.search) {
            window.history.replaceState(null, '', newUrl);
        }
    }, [page, limit, verified, rated, country, stateRegion, startDate, endDate, query, sortBy, sortDir, location.pathname]);

    // Fetch current admin role and total users once
    useEffect(() => {
        const fetchTotals = async () => {
            try {
                const resp = await axios.get('/origin/get-users-count', { headers: { 'x-access-token': token } });
                setGlobalTotal(resp?.data?.count || 0);
            } catch (e) {
                // Non-fatal
            }
        };
        const fetchRole = async () => {
            try {
                const resp = await axios.get('/origin/get-current-admin', { headers: { 'x-access-token': token } });
                setCurrentAdminRole(resp?.data?.role || '');
            } catch {}
        };
        fetchTotals();
        fetchRole();
    }, [token]);

        // Fetch users with filters/pagination
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                await checkAuthenticatedAdmin();
            } catch (err) {
                return navigate('/');
            }
            try {
                setLoading(true);
                dispatch(setFetching(true));
                const params = new URLSearchParams();
                params.set('page', String(page));
                params.set('limit', String(limit));
                if (verified !== '') params.set('verified', verified);
                if (rated !== '') params.set('rated', rated);
                if (country) params.set('country', country);
                if (stateRegion) params.set('state', stateRegion);
                if (startDate) params.set('startDate', startDate);
                if (endDate) params.set('endDate', endDate);
            if (query) params.set('q', query);
                if (sortBy) params.set('sortBy', sortBy);
                if (sortDir) params.set('sortDir', sortDir);

                const response = await axios.get(`/origin/get-users?${params.toString()}`, {
                    headers: { 'x-access-token': token },
                });
                if (response.status === 200) {
                    setUsers(response.data.users || []);
                    setTotal(response.data.total || 0);
                }
            } catch (error) {
                setError(error?.response?.data?.error || 'Failed to fetch users');
            } finally {
                setLoading(false);
                dispatch(setFetching(false));
            }
        };
            fetchUsers();
        }, [page, limit, verified, rated, country, stateRegion, startDate, endDate, query, sortBy, sortDir, token, dispatch, navigate, setError]);

        // Fetch filtered stats for KPIs
        useEffect(() => {
            const fetchStats = async () => {
                try {
                    const params = new URLSearchParams();
                    if (verified !== '') params.set('verified', verified);
                    if (rated !== '') params.set('rated', rated);
                    if (country) params.set('country', country);
                    if (stateRegion) params.set('state', stateRegion);
                    if (startDate) params.set('startDate', startDate);
                    if (endDate) params.set('endDate', endDate);
                    if (query) params.set('q', query);
                    const resp = await axios.get(`/origin/get-users-stats?${params.toString()}`, { headers: { 'x-access-token': token } });
                    setStats(resp?.data || { filteredTotal: 0, filteredVerified: 0, filteredRated: 0, newInRange: 0 });
                } catch (e) {
                    // non-fatal
                }
            };
            fetchStats();
        }, [verified, rated, country, stateRegion, startDate, endDate, query, token]);

        const onSortChange = (key) => {
            setSortDir((prevDir) => (sortBy === key ? (prevDir === 'asc' ? 'desc' : 'asc') : 'asc'));
            setSortBy(key);
            setPage(1);
        };

    // KPIs derived from loaded page
    const kpis = useMemo(() => {
            const loaded = users.length;
        const verifiedCount = users.filter(u => !!u.verified).length;
        const ratedCount = users.filter(u => !!u.rated).length;
            // Optional KPI: new users in date range (server returns only filtered page; this is client-side approximation for loaded slice)
            const newUsersInRange = users.filter(u => {
                if (!startDate && !endDate) return false;
                const t = new Date(u.createdAt).getTime();
                const s = startDate ? new Date(startDate + 'T00:00:00Z').getTime() : -Infinity;
                const e = endDate ? new Date(endDate + 'T23:59:59Z').getTime() : Infinity;
                return t >= s && t <= e;
            }).length;
            return { loaded, verifiedCount, ratedCount, newUsersInRange };
        }, [users, startDate, endDate]);

    const resetFilters = () => {
        setVerified('');
        setRated('');
        setCountry('');
        setStateRegion('');
        setStartDate('');
        setEndDate('');
        setQuery('');
        setPage(1);
    };

    const exportCSV = () => {
        const cols = [
            'firstName','lastName','email','dob','mobile','streetCity','stateRegion','country','verified','rated','myIp','createdAt'
        ];
        const escapeCell = (val) => {
            if (val === null || val === undefined) return '';
            const str = String(val).replace(/"/g, '""');
            if (/[",\n]/.test(str)) return '"' + str + '"';
            return str;
        };
        const header = cols.join(',');
        const rows = users.map(u => cols.map(c => escapeCell(u[c])).join(','));
        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_page${page}_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className={styles.analyticsContainer}>
            <AdminSideMenu />
            <div className={styles.analyticsContent}>
                {/* Header */}
                <div className={styles.analyticsHeader}>
                    <div className={styles.headerLeft}>
                        <Typography variant="h4" className={styles.pageTitle}>Users</Typography>
                        <Typography variant="body2" className={styles.pageSubtitle}>Manage and analyze registered users</Typography>
                    </div>
                </div>

                {/* Filter toolbar */}
                <Box sx={{ background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                      <DebouncedTextField size="small" label="Search (name/email/ip)" value={query} onChange={(val) => { setQuery(val); setPage(1); }} delay={400} />
                    <TextField size="small" label="Country" value={country} onChange={(e) => { setCountry(e.target.value); setPage(1); }} />
                    <TextField size="small" label="State/Region" value={stateRegion} onChange={(e) => { setStateRegion(e.target.value); setPage(1); }} />
                    <Select size="small" value={verified} displayEmpty onChange={(e) => { setVerified(e.target.value); setPage(1); }}>
                        <MenuItem value=""><em>Verified: Any</em></MenuItem>
                        <MenuItem value="true">Verified</MenuItem>
                        <MenuItem value="false">Unverified</MenuItem>
                    </Select>
                    <Select size="small" value={rated} displayEmpty onChange={(e) => { setRated(e.target.value); setPage(1); }}>
                        <MenuItem value=""><em>Rated: Any</em></MenuItem>
                        <MenuItem value="true">Rated</MenuItem>
                        <MenuItem value="false">Not Rated</MenuItem>
                    </Select>
                    <TextField size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
                    <TextField size="small" type="date" label="End" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        <Button variant="outlined" onClick={resetFilters}>Reset</Button>
                        {currentAdminRole === 'superadmin' && (
                            <Button variant="contained" onClick={exportCSV}>Export CSV</Button>
                        )}
                    </Box>
                </Box>

                {/* KPI cards */}
                        <Grid container spacing={2} className={styles.kpiContainer}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                            <CardContent>
                                <Typography className={styles.kpiLabel}>Loaded / Filtered</Typography>
                                        <Typography className={styles.kpiValue}>{kpis.loaded} / {stats.filteredTotal || total}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                            <CardContent>
                                <Typography className={styles.kpiLabel}>Total Users</Typography>
                                <Typography className={styles.kpiValue}>{globalTotal}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                            <CardContent>
                                        <Typography className={styles.kpiLabel}>Verified (filtered)</Typography>
                                        <Typography className={styles.kpiValue}>{stats.filteredVerified ?? kpis.verifiedCount}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                            <CardContent>
                                        <Typography className={styles.kpiLabel}>Rated (filtered)</Typography>
                                        <Typography className={styles.kpiValue}>{stats.filteredRated ?? kpis.ratedCount}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                                    <CardContent>
                                        <Typography className={styles.kpiLabel}>New (range)</Typography>
                                        <Typography className={styles.kpiValue}>{stats.newInRange || 0}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                </Grid>

                {/* Table */}
                        <Box className={styles.chartContainer} sx={{ p: 0 }}>
                            <UsersTable allUsers={users} sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />
                </Box>

                {/* Pagination controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
                    <Button disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
                    <Typography variant="body2">Page {page}</Typography>
                    <Button disabled={page * limit >= total || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
                    <Select size="small" value={String(limit)} onChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(1); }}>
                        {[50, 100, 200, 500].map(n => <MenuItem key={n} value={String(n)}>{n}/page</MenuItem>)}
                    </Select>
                </Box>
            </div>
        </div>
    );
};

        // Small debounce wrapper for TextField
        const DebouncedTextField = ({ value, onChange, delay = 300, ...props }) => {
            const [inner, setInner] = useState(value || '');
            const timer = useRef(null);
            useEffect(() => { setInner(value || ''); }, [value]);
            useEffect(() => {
                clearTimeout(timer.current);
                timer.current = setTimeout(() => onChange && onChange(inner), delay);
                return () => clearTimeout(timer.current);
            }, [inner, delay, onChange]);
            return <TextField {...props} value={inner} onChange={(e) => setInner(e.target.value)} />;
        };

export default UsersDashboard;
